import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardContent, CardHeader, MoneyDisplay } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { useCategoriesQuery, useCategoryName } from '@entities/taxonomy';
import type { BuyRequest, RfqStatus } from '@entities/rfq';
import { canSubmitAgainst } from '@entities/rfq';

const PREVIEW_LIMIT = 200;

const STATUS_TONE: Record<RfqStatus, 'success' | 'neutral' | 'warning'> = {
  OPEN: 'success',
  CLOSED: 'neutral',
  EXPIRED: 'warning',
};

const STATUS_KEY: Record<RfqStatus, string> = {
  OPEN: 'buyRequests.card.open',
  CLOSED: 'buyRequests.card.closed',
  EXPIRED: 'buyRequests.card.expired',
};

export function BuyRequestCard({ rfq }: { rfq: BuyRequest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Feed needs both PRODUCT (RFQ targets a product category) and PRODUCER
  // for completeness — fetch unfiltered to match against any kind.
  const { data: categories } = useCategoriesQuery();
  const localize = useCategoryName();
  const cat = categories?.find((c) => c.id === rfq.category);
  const categoryNameText = cat ? localize(cat) : rfq.category;
  const isTargeted = rfq.dispatch === 'TARGETED';
  const canRespond = canSubmitAgainst(rfq);
  const preview = rfq.techDescription
    ? rfq.techDescription.length > PREVIEW_LIMIT
      ? `${rfq.techDescription.slice(0, PREVIEW_LIMIT).trim()}…`
      : rfq.techDescription
    : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold">{rfq.name}</h3>
            {isTargeted ? <Badge tone="primary">{t('buyRequests.card.targeted')}</Badge> : null}
            {rfq.alreadyQuoted ? <Badge tone="info">{t('buyRequests.card.quoted')}</Badge> : null}
            <Badge tone={STATUS_TONE[rfq.status]}>{t(STATUS_KEY[rfq.status])}</Badge>
            {rfq.hiddenByAdmin ? <Badge tone="danger">{t('buyRequests.card.hidden')}</Badge> : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{categoryNameText}</span>
            {rfq.hsCode ? <span>HS {rfq.hsCode}</span> : null}
            {rfq.destinationCountry ? <span>→ {rfq.destinationCountry}</span> : null}
            {rfq.volumeText ? <span>{rfq.volumeText}</span> : null}
            {rfq.deadline ? (
              <span>
                {t('buyRequests.detail.fields.deadline')}: {new Date(rfq.deadline).toLocaleDateString()}
              </span>
            ) : null}
            <span>{t('buyRequests.detail.fields.quotes', { count: rfq.quoteCount })}</span>
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="font-medium">
            <MoneyDisplay money={rfq.budget} fallback={t('buyRequests.detail.fields.budget')} />
          </div>
          <div className="text-xs opacity-70">{rfq.attachments.length}</div>
        </div>
      </CardHeader>
      {preview ? (
        <CardContent>
          <p className="text-sm text-muted-foreground">{preview}</p>
        </CardContent>
      ) : null}
      <CardContent className="flex justify-end gap-2 pt-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`${ROUTES.buyRequests}/${rfq.id}`)}
        >
          {t('buyRequests.card.openDetail')}
        </Button>
        <Button
          size="sm"
          variant={rfq.alreadyQuoted ? 'outline' : 'default'}
          disabled={!rfq.alreadyQuoted && !canRespond}
          title={!rfq.alreadyQuoted && !canRespond ? t('buyRequests.detail.notOpenTip') : undefined}
          onClick={() => navigate(`${ROUTES.buyRequests}/${rfq.id}/respond`)}
        >
          {rfq.alreadyQuoted ? t('buyRequests.card.viewQuote') : t('buyRequests.card.respond')}
        </Button>
      </CardContent>
    </Card>
  );
}
